import { useRef, useEffect, useState } from "react";
import { toast } from "sonner";

/**
 * ARBackground — Renders the device camera feed as a fullscreen <video>
 * positioned BEHIND the 3D canvas. Caller is responsible for making the
 * canvas transparent.
 *
 * Returns { active, error, requestPermission, stop }.
 */
export function useARBackground() {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [active, setActive] = useState(false);
  const [error, setError] = useState(null);

  const stop = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setActive(false);
  };

  const requestPermission = async () => {
    setError(null);
    if (!navigator.mediaDevices?.getUserMedia) {
      setError("Tu navegador no soporta acceso a la cámara");
      toast.error("Cámara no disponible en este navegador");
      return false;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
      setActive(true);
      toast.success("Modo AR activado");
      return true;
    } catch (err) {
      const msg = err?.name === "NotAllowedError"
        ? "Permiso de cámara denegado"
        : err?.name === "NotFoundError"
          ? "No se encontró cámara"
          : "Error al activar cámara";
      setError(msg);
      toast.error(msg);
      return false;
    }
  };

  useEffect(() => () => stop(), []);

  return { videoRef, active, error, requestPermission, stop };
}

export function ARVideoLayer({ videoRef, visible }) {
  return (
    <video
      ref={videoRef}
      autoPlay
      playsInline
      muted
      className="absolute inset-0 w-full h-full object-cover"
      style={{
        display: visible ? "block" : "none",
        zIndex: 0,
        transform: "scaleX(1)",
      }}
      data-testid="ar-video-layer"
    />
  );
}
